import React, { useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { app, media } from 'context';
import { APP_BANNER_HEIGHT, BOTTOM_NAVIGATION_HEIGHT, Clickable, IconButton, Layout, StickyContainer, Text } from 'ds';
import { AI_ASSISTANT_PATH } from 'routes';
import { actions } from 'store/AIAssistant';
import { selectAIAssistantConversationsAndGroupByMonth } from 'store/AIAssistant/selectors';
import { selectUIState } from 'store/UI/selectors';
import { selectUser } from 'store/User/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { AI_ASSISTANT_BOTTOM_NAVIGATION_HEIGHT, AI_ASSISTANT_NAVIGATION_HEADER_HEIGHT } from './constants';
import useAIAssistantHeight from './useAIAssistantHeight';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  loadedFirstTime: boolean;
  setLoadedFirstTime: (loadedFirstTime: boolean) => void;
  sideBarIsAlreadyShown: boolean;
}

const AIAssistantConversations: React.FC<Props> = ({
  isVisible,
  onClose,
  loadedFirstTime,
  setLoadedFirstTime,
  sideBarIsAlreadyShown
}) => {
  const user = useAppSelector(selectUser);
  const { isMobile } = useContext(media);
  const { appBannerType } = useAppSelector(selectUIState);
  const { windowWidth } = useContext(app);
  const { id: conversationIdSelected } = useParams<{ id: string | undefined }>();
  const conversationsByMonth = useAppSelector(selectAIAssistantConversationsAndGroupByMonth);
  const height = useAIAssistantHeight();
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isVisible && !loadedFirstTime) {
      setLoadedFirstTime(true);
    }
  }, [isVisible, loadedFirstTime, setLoadedFirstTime]);

  if (!user || !loadedFirstTime) return null;

  function toSentenceCase(str: string) {
    return str
      .toLowerCase()
      .replace(/(^\s*\w|[.!?]\s*\w)/g, match => match.toUpperCase())
      .replace(/\bcodi\b/gi, 'Codi');
  }

  return (
    <Layout
      overflowY="auto"
      direction="column"
      justify="flex-start"
      align="flex-start"
      color="gray-10"
      height="100%"
      opacity={1}
      top={appBannerType ? APP_BANNER_HEIGHT : 0}
      {...(isMobile ? { left: 0, width: windowWidth } : { left: 60, width: 320 })}
      flex
      paddingX={24}
      paddingBottom={24}
      position="fixed"
      overflow="hidden"
      className={`threads-sidebar ${sideBarIsAlreadyShown ? '' : isVisible ? 'slide-in' : 'slide-out'}`}
      zIndex={1}
    >
      <StickyContainer width="100%" top={0} zIndex={2}>
        <Layout
          width="100%"
          direction="row"
          justify={'space-between'}
          align="center"
          color="gray-10"
          paddingTop={isMobile ? 12 : 24}
        >
          <IconButton
            iconColor="gray-400"
            name={isMobile ? 'sort' : 'sideMenu'}
            size={isMobile ? 'lg' : 'md'}
            onClick={onClose}
            type="noBackground"
            stroke={isMobile ? 1.5 : 2}
          />
          <IconButton
            iconColor="gray-400"
            name="createPen"
            size={isMobile ? 'lg' : 'md'}
            onClick={() => {
              history.push({ pathname: AI_ASSISTANT_PATH, state: { autoFocus: true } });
              if (isMobile) {
                onClose();
              }
            }}
            type="noBackground"
            stroke={isMobile ? 1.5 : 2}
          />
        </Layout>
      </StickyContainer>
      <Layout
        {...(isMobile
          ? {}
          : { height: height - AI_ASSISTANT_NAVIGATION_HEADER_HEIGHT - AI_ASSISTANT_BOTTOM_NAVIGATION_HEIGHT })}
        flex
        direction="column"
        gap={12}
        marginTop={12}
        height="fit-content"
        width="100%"
        paddingBottom={isMobile ? BOTTOM_NAVIGATION_HEIGHT : 20}
        zIndex={1}
      >
        {conversationsByMonth.map(conversationGroup => (
          <Layout key={conversationGroup.conversations[0]?.created_at} direction="column" gap={12}>
            <Layout marginLeft={24}>
              <Text size="body-xs" color="gray-400" semibold>
                {conversationGroup.name}
              </Text>
            </Layout>
            <Layout flex direction="column">
              {conversationGroup.conversations.map(({ id: conversationId, ai_assistant_messages }) => {
                if (ai_assistant_messages.length === 0) return '';

                const messagePreview = ai_assistant_messages[0].content;

                return (
                  <Clickable
                    onClick={e => {
                      e.stopPropagation();
                      if (isMobile) {
                        dispatch(actions.setSidebarVisible(false));
                      }
                      history.push(`${AI_ASSISTANT_PATH}/${conversationId}`);
                    }}
                    key={conversationId}
                  >
                    <Layout
                      paddingRight={8}
                      paddingLeft={24}
                      height={48}
                      borderRadius={8}
                      hoverColor={conversationIdSelected === conversationId.toString() ? undefined : 'gray-50'}
                      color={
                        conversationIdSelected === conversationId.toString() ? 'purple-gradient-transparent' : 'gray-10'
                      }
                      align="center"
                      maxWidth={isMobile ? '95%' : 272}
                    >
                      <Text size="body-sm" align="left" ellipsis>
                        {toSentenceCase(messagePreview)}
                      </Text>
                    </Layout>
                  </Clickable>
                );
              })}
            </Layout>
          </Layout>
        ))}
      </Layout>
    </Layout>
  );
};

export default AIAssistantConversations;
